import { CSSProperties, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useAddRejectionReason,
  useFarmingStagesByContractId,
  useGetContractById,
  useGetRejectionReasonsByContractById,
  useRetryLoanForEntitlement,
  useUpdateContractStatus,
  useUpdateRejectionLoan,
  useUpdateRejectionStatus,
} from "../../api/contract";
import {
  Button,
  Card,
  Input,
  message,
  Modal,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { Image } from "antd";
import {
  GoogleMap,
  InfoWindow,
  MarkerF,
  useLoadScript,
} from "@react-google-maps/api";

import img from "../../assets/images/user.png";
import female from "../../assets/images/user-female.png";
import { FarmLandCard, IAddress } from "../customer/CustomerDetailPage";
import OrderTrackingGraph from "../../components/OrderTrackingGraph";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import pdfIcon from "../../assets/images/pdf.png";
import LoadingScreen from "../../components/LoadingScreen";
import VerifyCustomer from "../../components/VerifiyCustomer";
import type {
  ContractFarmerBankAccount,
  FinancingBankSummary,
} from "../../api/contract/types";
import {
  isRetryableLoanStatus,
  retryLabelForStatus,
} from "../../api/contract/types";
import { ColumnsType } from "antd/es/table";
import ActionMenu from "../../components/shared/ActionMenu";
import {
  getEntitlementLoanStatusPresentation,
  getStatusText,
  getVoucherStatusConfig,
} from "../../util/status-formatter";
import { CheckCircleIcon, CircleAlert } from "lucide-react";
import { formatNumber } from "../../util/price-formatter";
import { extractMessage } from "../../util/extract-message";

interface Entitlement {
  id?: number;
  inputName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  landSize: number;
  voucherStatus: number;
  /**
   * Granular loan lifecycle from `EntitlmentDetailDto.loanStatus` - one of
   * `EntitlementLoanStatus`. Drives the chip colour and the Retry affordance.
   */
  loanStatus?: string;
  /**
   * Sanitized message from the bank/Qena when the most recent phase failed.
   * Only populated when `loanStatus` ends with `_FAILED`.
   */
  loanFailureReason?: string | null;
  /**
   * Mirrors `tbl_loan_config.require_document_verification` for the bank +
   * product behind this entitlement. When false, Qena docs are not part of
   * the loan flow for that route, so `docsVerified` / `lastDocVerifiedAt`
   * come back null and the docs UI affordance is hidden entirely.
   */
  requiresDocVerification?: boolean | null;
  /**
   * Mirrors `LoanTransaction.docsVerified` - shown as an info tag on the row.
   * Always null when `requiresDocVerification` is false.
   */
  docsVerified?: boolean | null;
  /**
   * ISO timestamp of the last docs-verification callback from Qena.
   * Always null when `requiresDocVerification` is false.
   */
  lastDocVerifiedAt?: string | null;
  /** Operator-driven retry count from `LoanTransaction.retryCount`. */
  retryCount?: number | null;
}

const wrapper: CSSProperties = {
  display: "flex",
  width: "100%",
  gap: 10,
  fontSize: 16,
};
const label: CSSProperties = {
  color: "#128807",
  fontWeight: 500,
  fontSize: 20,
};
const value: CSSProperties = {
  color: "#8590A3",
  fontWeight: 500,
  fontSize: 16,
  alignItems: "center",
  display: "flex",
};

const getStatusColor = (status: string) => {
  let color = "#FFC107";
  switch (status) {
    case "REDEEMED":
      color = "#32a852";
      break;
    case "APPROVED":
      color = "#32a852";
      break;
    case "PARTIALLY REDEEMED":
      color = "#FFA500";
      break;
    case "PENDING":
      color = "#FFA500";
      break;
    case "ISSUED":
      color = "#cc5866";
      break;
    case "CANCELED":
      color = "#cc5866";
      break;
    case "VOID":
      color = "#555555";
      break;
    default:
      color = "#FFC107";
      break;
  }

  return color;
};

const insuranceColumns = [
  {
    dataIndex: "companyName",
    title: "Company",
    render: (companyName: string, record: any) => (
      <div className="flex flex-col">
        <div>{companyName}</div>
        <div className="text-gray-500">{record.phoneNumber}</div>
      </div>
    ),
  },
  {
    dataIndex: "proposalNumber",
    title: "Proposal No.",
  },
  {
    dataIndex: "enrollmentDate",
    title: "Enrollment Date",
  },
  {
    dataIndex: "premium",
    title: "Premium (ETB)",
  },
  {
    dataIndex: "sumInsured",
    title: "Sum Insured",
  },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: string) => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            minWidth: 100,
            width: "80%",
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8.5,
            color: getStatusColor(status),
            backgroundColor: `${getStatusColor(status)}44`,
          }}
        >
          {status}
        </div>
      );
    },
  },
];

const ContractDetailPage = () => {
  const { TextArea } = Input;
  const [photo, setPhoto] = useState<string | null | undefined>();
  const [documents, setDocuments] = useState<
    | {
        docId: number;
        customerId: number;
        file: string;
        fileName: string;
        fileType: string;
        status?: string;
      }[]
    | null
    | undefined
  >([]);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBG4pD21Xf2Mqq0kMUo2POHq_UrHooGQ18",
  });

  const { id } = useParams();
  const { user } = useAuth() as AuthContextValue;

  const [mapView, setMapView] = useState<boolean>(false);
  const [farmLands, setFarmLands] = useState<IAddress[]>([]);
  const [activeMarker, setActiveMarker] = useState("");
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [tableData, setTableData] = useState<Entitlement[]>([]);

  const [insuranceTableData, setInsuranceTableData] = useState<
    {
      companyName: string;
      phoneNumber: string;
      proposalNumber: string;
      enrollmentDate: string;
      premium: string;
      sumInsured: string;
      status: string;
    }[]
  >([]);
  const {
    data,
    isLoading: contractLoading,
    refetch: fetchContract,
  } = useGetContractById(id);

  // Multi-bank rollout: KYC and Qena linkage now live on the contract's
  // FarmerBankAccount, not on the customer. The same farmer can have one
  // account per bank, each with its own kycStatus / bankCustomerId. Pull the
  // bound bank + account once and drive every banking-aware UI off these.
  // Both can be `null` for legacy contracts not yet bound to a bank - in that
  // case Qena-bound actions (KYF approve/reject, document upload, loan
  // approve/reject) must be hidden.
  const detail = data?.data?.data as
    | {
        farmerBankAccount?: ContractFarmerBankAccount | null;
        financingBank?: FinancingBankSummary | null;
      }
    | undefined;
  const farmerBankAccount = detail?.farmerBankAccount ?? null;
  const financingBank = detail?.financingBank ?? null;
  const kycApproved = farmerBankAccount?.kycApproved ?? false;
  const isContractBoundToBank = !!financingBank && !!farmerBankAccount;

  const { mutateAsync: updateStatus, isPending } = useUpdateContractStatus();
  const {
    mutateAsync: updateRejectionStatus,
    isPending: isRejectionStatusUpdatePending,
  } = useUpdateRejectionStatus();
  const { mutateAsync: addRejectionReason } = useAddRejectionReason();
  const { mutateAsync: approveRejectLoan } = useUpdateRejectionLoan();
  const { mutateAsync: retryLoanForEntitlement, isPending: isRetryingLoan } =
    useRetryLoanForEntitlement();
  const [retryingEntitlementId, setRetryingEntitlementId] = useState<
    number | null
  >(null);

  const getEntitlementColumns = (
    type: "summary" | "details"
  ): ColumnsType<Entitlement> => {
    const base: ColumnsType<Entitlement> = [
      {
        dataIndex: "inputName",
        title: "Input Name",
      },
      {
        dataIndex: "quantity",
        title: "Quantity (Kg)",
      },
      {
        dataIndex: "unitPrice",
        title: "Unit Price",
        render: (value: number) => `${formatNumber(value)}`,
        align: "right",
      },
      {
        dataIndex: "totalPrice",
        title: "Total Price",
        render: (value: number) => `${formatNumber(value)}`,
        align: "right",
      },
      {
        dataIndex: "landSize",
        title: "Land Size (Hac)",
        align: "center",
      },
      {
        dataIndex: "voucherStatus",
        title: "Status",
        render: (status: number) => {
          const _status = getStatusText(status);

          if (!_status) return <span className="text-gray-400">-</span>;

          const statusConfig = getVoucherStatusConfig(status);
          const formattedText = _status
            .toLowerCase()
            .split("_")
            .map((word, i) =>
              i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
            )
            .join(" ");

          return (
            <div
              className={`
                inline-flex items-center justify-center
                min-w-[100px] max-w-[140px]
                px-3 py-1.5
                rounded-full
                text-xs font-medium
                border transition-all duration-200
                ${statusConfig.className}
              `}
              title={`Status: ${formattedText}`}
            >
              <div
                className="w-1.5 h-1.5 rounded-full mr-2 animate-pulse"
                style={{ backgroundColor: statusConfig.dotColor }}
              />
              {formattedText}
            </div>
          );
        },
      },
      {
        dataIndex: "loanStatus",
        title: "Loan Status",
        render: (status: string, record: Entitlement) => {
          if (!status) return <span className="text-gray-400">-</span>;

          const presentation = getEntitlementLoanStatusPresentation(status);
          const chipLabel = presentation.label;
          const isFailed = status.endsWith("_FAILED");
          const failureReason = record.loanFailureReason?.trim() || null;
          // Failure tooltip shows the sanitized Qena reason; on success we
          // still expose the full status name so the chip stays accessible
          // when truncated by max-width.
          const tooltipTitle = isFailed
            ? failureReason ||
              "Failed at bank - retry the action or check audit logs."
            : `Loan Status: ${chipLabel}`;

          const chip = (
            <div
              className={`
                inline-flex items-center justify-center
                min-w-[100px] max-w-[160px]
                px-3 py-1.5
                rounded-full
                text-xs font-medium
                border transition-all duration-200
                ${presentation.className}
              `}
            >
              <div
                className="w-1.5 h-1.5 rounded-full mr-2 animate-pulse"
                style={{ backgroundColor: presentation.dotColor }}
              />
              {chipLabel}
            </div>
          );

          // Docs-verified affordance only renders when the bank/product
          // route requires docs (`tbl_loan_config.require_document_verification
          // = true`). For routes where docs aren't part of the loan flow we
          // hide the tag entirely so it doesn't read as missing/failed.
          const docsRequired = record.requiresDocVerification === true;
          return (
            <div className="flex flex-col items-start gap-1">
              <Tooltip title={tooltipTitle}>{chip}</Tooltip>
              {docsRequired && record.docsVerified ? (
                <Tag
                  color="green"
                  className="m-0 border-0 text-[10px] font-medium"
                >
                  Docs verified
                </Tag>
              ) : null}
              {typeof record.retryCount === "number" && record.retryCount > 0 ? (
                <span className="text-[10px] text-gray-500">
                  Retried {record.retryCount}×
                </span>
              ) : null}
            </div>
          );
        },
      },
    ];

    // Action column only for details table
    if (type === "details") {
      // Only push the "Action" column if the user is BANK_ADMIN or BANK_OPERATION
      const isBankUser =
        user?.roleName === "WEB_BANK_ADMIN" ||
        user?.roleName === "WEB_BANK_OPERATION" ||
        user?.roleName === "ROLE_MFI_ADMIN";

      if (isBankUser) {
        base.push({
          title: "Action",
          key: "action",
          render: (_, record) => {
            // Approve / Reject only make sense on a fresh entitlement
            // (LoanStatus.PENDING). Once the bank has acted, the operator
            // either lets the lifecycle complete or, on a `_FAILED` state,
            // uses the phase-targeted Retry button driven by the same row.
            const status = record.loanStatus;
            const isPending = !status || status === "PENDING";
            const isRetryable = isRetryableLoanStatus(status);
            const isThisRowRetrying =
              isRetryingLoan && retryingEntitlementId === record.id;

            return (
              <ActionMenu
                actions={[
                  {
                    type: "approve",
                    label: "Approve",
                    onClick: () => handleApprove(record),
                    disabled: !kycApproved || !isPending,
                  },
                  {
                    type: "reject",
                    label: "Reject",
                    onClick: () => handleReject(record),
                    disabled: !kycApproved || !isPending,
                  },
                  {
                    type: "retry",
                    label: retryLabelForStatus(status),
                    onClick: () => handleRetry(record),
                    disabled:
                      !kycApproved || !isRetryable || isThisRowRetrying,
                  },
                ]}
                iconSize="md"
                variant="outline"
              />
            );
          },
        });
      }
    }

    // Determine hidden columns
    let hiddenColumns: string[] = [];
    if (type === "summary") hiddenColumns = ["loanStatus", "action"];
    if (type === "details") hiddenColumns = ["voucherStatus"];

    return base.filter(
      (col) =>
        !("dataIndex" in col && hiddenColumns.includes(col.dataIndex as string))
    );
  };

  const { data: allRejectionReasons, refetch: fetchRejectionReasons } =
    useGetRejectionReasonsByContractById(id);

  const [selectedRejectionStatusId, setSelectedRejectionStatusId] =
    useState(-1);
  const [rejectionHistory, setRejectionHistory] = useState<
    { id: number; reason: string; status: "ACTIVE" | "RESOLVED" }[]
  >([]);

  const [inActiveRejections, setInActiveRejections] = useState(0);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejectionStatusPending, setIsRejectionStatusUpdatePending] =
    useState(false);

  const [expandedList, setExpandedList] = useState<
    { id: number; isExpanded: boolean }[]
  >([]);

  const { data: trackingData, refetch } = useFarmingStagesByContractId(id);

  const [graphData, setGraphData] = useState<any>([]);
  const [activeTab, setActiveTab] = useState("1");

  useEffect(() => {
    const val = rejectionHistory.reduce<{ active: number; inactive: number }>(
      (acc, item) => {
        const key = item.status.toLowerCase() as keyof {
          active: number;
          inactive: number;
        };
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );

    setInActiveRejections(val.inactive);
    const _expandedList: { id: number; isExpanded: boolean }[] =
      rejectionHistory.map(
        (item: {
          id: number;
          reason: string;
          status: "ACTIVE" | "RESOLVED";
        }) => {
          return { id: item.id, isExpanded: false };
        }
      );

    setExpandedList(_expandedList);
  }, [rejectionHistory]);

  useEffect(() => {
    if (
      allRejectionReasons &&
      allRejectionReasons.data &&
      allRejectionReasons.data.data
    ) {
      const reasons = allRejectionReasons.data.data;
      setRejectionHistory(reasons);
    }
  }, [allRejectionReasons]);

  useEffect(() => {
    if (id) {
      fetchContract();
      fetchRejectionReasons();
    }
  }, [id]);

  useEffect(() => {
    if (data && data.data && data.data.data) {
      //     setCenter(
      //         { lat: data.data.data.customerDetail.farmLand.latitude as number, lng: data.data.data.customerDetail.farmLand.longitude as number });
    }
    if (
      data &&
      data.data &&
      data.data.data &&
      data.data.data?.customerDetail.farmerDocuments
    ) {
      const _photo = data.data.data?.customerDetail.farmerDocuments.find(
        (item: any) => item.fileName.toLowerCase().indexOf("photo") > -1
      );
      if (_photo) {
        setPhoto(_photo.file);
      }

      const _documents = data.data.data?.customerDetail.farmerDocuments.filter(
        (item: any) => !item.fileName.startsWith("photo")
      );

      if (_documents) {
        const d = _documents.map((item: any) => {
          return {
            docId: item.docId,
            file: item.file,
            fileName: item.fileName,
            fileType: item.fileType,
          };
        });

        setDocuments(d);
      }
    }

    if (data && data.data && data.data.data && data.data.data.entitlements) {
      const _tableData: Entitlement[] = data.data.data.entitlements.map(
        (item: any) => {
          return {
            id: item.id,
            inputName: item.input.inputName,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
            landSize: item.landSize,
            voucherStatus: item.voucherStatus,
            loanStatus: item.loanStatus,
            // New multi-bank retry fields hydrated from LoanTransaction by
            // EntitlmentDetailMapper#hydrateLoanTransactionFields. Default
            // to nullish so legacy rows don't show "Retried 0×" or stale
            // "Docs verified" tags.
            loanFailureReason: item.loanFailureReason ?? null,
            requiresDocVerification: item.requiresDocVerification ?? null,
            docsVerified: item.docsVerified ?? null,
            lastDocVerifiedAt: item.lastDocVerifiedAt ?? null,
            retryCount: item.retryCount ?? null,
          };
        }
      );

      if (_tableData) {
        setTableData(_tableData);
      }
    }

    if (data && data.data && data.data.data && data.data.data.insurance) {
      const _data = {
        companyName: data.data.data.insurance.insuranceCompany.orgName,
        phoneNumber: data.data.data.insurance.insuranceCompany.phoneNo,
        proposalNumber: data.data.data.insurance.proposalNumber,
        enrollmentDate: data.data.data.insurance.enrollmentDate,
        premium: data.data.data.insurance.premium.toLocaleString(),
        sumInsured: data.data.data.insurance.sumInsured.toLocaleString(),
        status: data.data.data.insurance.enrolmentStatus,
      };

      setInsuranceTableData([_data]);
    }

    if (data && data.data && data.data.data && data.data.data?.customerDetail) {
      if (
        data.data.data?.customerDetail.farmLand &&
        data.data.data?.customerDetail.farmLand.length > 0
      ) {
        const item = data.data.data?.customerDetail?.farmLand[0];
        const land: IAddress = {
          latitude: item?.latitude,
          longitude: item?.longitude,
          landCode: item?.landCode,
          landSize: item?.landSize,
          region: "",
          zone: "",
          woreda: "",
          kebele: "",
          unionName: "",
          primaryCooperativeName: "",
        };

        if (item?.address) {
          const _address = JSON.parse(item.address);
          land.region = _address.region;
          land.woreda = _address.woreda;
          land.zone = _address.zone;
          land.kebele = _address.kebele;
          land.unionName = _address.unionName;
          land.primaryCooperativeName = _address?.primaryCooperativeName;
        }

        setFarmLands([land]);
      }
    }
  }, [data]);

  const entitlements = data?.data?.data?.entitlements;
  const totalEntitlementsPrice = entitlements?.reduce(
    (accumulator: number, entitlement: { totalPrice: number }) => {
      return accumulator + entitlement.totalPrice;
    },
    0
  );

  const handleOnLoad = (map: any) => {
    const bounds = new google.maps.LatLngBounds();
    map.fitBounds(bounds);
  };

  const handleActiveMarker = (landCode: string) => {
    if (landCode === activeMarker) {
      return;
    }
    setActiveMarker(landCode);
  };

  const handleUpdateRejectionStatus = async (id: number, checked: boolean) => {
    try {
      const result = await updateRejectionStatus({ status: checked, id });

      if (result) {
        message.success("Status updated successfully");
        fetchContract();
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    } finally {
      fetchRejectionReasons();
    }
  };

  const handleAddRejectionReason = async () => {
    try {
      const result = await addRejectionReason({
        reason: rejectionReason,
        status: "ACTIVE",
        id: Number(id),
      });
      if (result) {
        message.success("add rejection reason successfully");
        fetchContract();
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    } finally {
      setRejectionReason("");
      setOpenRejectionModal(false);
      fetchRejectionReasons();
    }
  };

  useEffect(() => {
    if (
      trackingData &&
      trackingData.data &&
      trackingData.data.data &&
      trackingData.data.data.data
    ) {
      const val = Object.entries(trackingData.data.data.data).map((v: any) => {
        return { date: v[0], ...v[1] };
      });
      if (val) {
        setGraphData(val);
      }
    }
  }, [trackingData]);

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id]);

  const dobString = data?.data?.data?.customerDetail.customer.dateOfBirth;
  const dob = new Date(dobString);
  const formattedDate = dob.toLocaleDateString("en-GB");

  const formatValue = (value: { toLocaleString: () => any }) =>
    value ? value.toLocaleString() : "N/A";

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text: string) => text?.split(".")[0],
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
    },
    {
      title: "Preview",
      dataIndex: "preview",
      key: "preview",
      render: (text: any, record: any) =>
        record.fileType?.includes("pdf") ? (
          <Image preview={false} src={pdfIcon} width={50} height={50} />
        ) : (
          <Image
            src={`data:image/jpeg;base64,${record.file}`}
            height={50}
            width={50}
          />
        ),
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text: any, record: any) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          href={`data:application/pdf;base64,${record.file}`}
          download={`${data?.data?.data?.customerDetail?.customer?.fullName}-${
            record.fileName.split(".")[0]
          }`}
        />
      ),
    },
  ];

  // Click handlers
  const handleApprove = async (record: Entitlement) => {
    try {
      const result = await approveRejectLoan({
        bankVerification: true,
        contractId: data?.data.data.contract.id,
        entitlementDetailId: record.id!,
      });

      const msg = extractMessage(result?.data?.message);

      if (result.data?.status === "0") {
        message.error(msg);
        return;
      }

      message.success("Loan approved successfully");
      fetchContract();
    } catch (err: any) {
      const errMsg = extractMessage(err?.response?.data?.message);
      message.error(errMsg);
    }
  };

  const handleReject = async (record: Entitlement) => {
    try {
      const result = await approveRejectLoan({
        bankVerification: false,
        contractId: data?.data.data.contract.id,
        entitlementDetailId: record.id!,
      });
      if (result) {
        message.success("Loan rejected successfully");
        fetchContract();
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    }
  };

  /**
   * Re-runs the failed Qena phase for this entitlement. The backend inspects
   * `EntitlementDetail.loanStatus` and dispatches to exactly one of:
   *   - createLoan          (LOAN_REQUEST_FAILED)
   *   - addDisbursementAccount (DISBURSEMENT_ACCOUNT_FAILED)
   *   - disburseLoan        (DISBURSEMENT_FAILED)
   *
   * On the wire the controller returns the standard FEResponse envelope, so
   * a 200 with `status === "0"` still means the retry failed at the bank -
   * surface that message instead of a generic toast.
   */
  const handleRetry = async (record: Entitlement) => {
    if (!record.id) return;
    setRetryingEntitlementId(record.id);
    try {
      const result = await retryLoanForEntitlement({
        entitlementDetailId: record.id,
      });

      const msg = extractMessage(result?.data?.message);

      if (result.data?.status === "0") {
        message.error(msg || "Retry failed at bank.");
        fetchContract();
        return;
      }

      message.success(msg || `${retryLabelForStatus(record.loanStatus)} ok`);
      fetchContract();
    } catch (err: any) {
      const errMsg = extractMessage(err?.response?.data?.message);
      message.error(errMsg || "Retry failed.");
    } finally {
      setRetryingEntitlementId(null);
    }
  };

  const loanStatus = data?.data?.data?.contract?.loanStatus;

  if (contractLoading || isPending) return <LoadingScreen />;

  const loanDetails = [
    {
      label: "Credit Amount (ETB)",
      value: `${formatNumber(totalEntitlementsPrice)}`,
    },
    {
      label: "Input Credit (ETB)",
      value: `${formatNumber(totalEntitlementsPrice)}`,
    },
    {
      label: "Insurance Credit (ETB)",
      value: `${formatNumber(data?.data?.data?.contract?.insuranceCredit)}`,
    },
  ];

  const tabItems = [
    {
      key: "1",
      label: "CONTRACT DETAIL",
      children: (
        <div>
          {data &&
            data.data &&
            data.data.data &&
            data.data.data?.customerDetail &&
            !contractLoading && (
              <div
                style={{
                  fontFamily: "Poppins",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 8.5,
                  backgroundColor: "white",
                  height: "70vh",
                }}
              >
                <div className="flex mb-[25px] gap-6 w-full">
                  <div className="bg-white rounded-lg shadow-md p-4 flex w-full">
                    <div className="flex flex-col gap-4 w-full">
                      {" "}
                      {/* Ensure full width */}
                      <div
                        style={{
                          backgroundColor: "#E9EDF4",
                          padding: "15px",
                          borderRadius: "8.5px",
                          fontWeight: 500,
                          fontSize: "24px",
                          marginBottom: "30px",
                          color: "#334155",
                        }}
                        className="w-full"
                      >
                        Contract Detail
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                        <div style={wrapper}>
                          <div style={label}>Contract Date</div>
                          <div style={value}>
                            {data.data.data.contract.contractDate}
                          </div>
                        </div>
                        <div style={wrapper}>
                          <div style={label}>Product</div>
                          <div style={value}>
                            {data.data.data.contract.product}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Contract Price (ETB)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.contractPrice)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Quantity (Kg)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.quantity)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Spot Price (ETB)</div>
                          <div style={value}>
                            {formatValue(data.data.data.contract.spotPrice)}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Harvest Date</div>
                          <div style={value}>
                            {data.data.data.contract.harvestDate}
                          </div>
                        </div>
                        <div style={wrapper}>
                          <div style={label}>Contract No</div>
                          <div style={value}>
                            {data.data.data.contract.contractNumber}
                          </div>
                        </div>

                        <div style={wrapper}>
                          <div style={label}>Contract Status</div>
                          <Tag
                            className="p-1"
                            color={
                              data.data.data.contract.contractStatus
                                ?.trim()
                                .toLowerCase() === "signed"
                                ? "green"
                                : data.data.data.contract.contractStatus
                                    ?.trim()
                                    .toLowerCase() === "unsigned"
                                ? "red"
                                : "default"
                            }
                          >
                            {data.data.data.contract.contractStatus}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      key: "2",
      label: "FARMER DETAILS",
      children: (
        <div className="h-[70vh]">
          {data &&
            data.data &&
            data.data.data &&
            data.data.data?.customerDetail &&
            !contractLoading && (
              <div className="flex w-full mb-[25px] gap-6">
                {/* Left side: Farmer photo card */}
                <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center w-1/3">
                  {photo ? (
                    <Image
                      src={`data:image/jpeg;base64,${photo}`}
                      height={150}
                      width={150}
                      className="rounded-full"
                      alt="Farmer"
                    />
                  ) : (
                    <Image
                      src={
                        data?.data?.customerDetail?.customer?.gender ===
                        "Female"
                          ? female
                          : img
                      }
                      height={150}
                      width={150}
                      className="rounded-full"
                      alt="Default Profile"
                    />
                  )}
                </div>

                {/* Right side: Farmer details */}
                <div className="bg-white rounded-lg shadow-md p-6 w-2/3">
                  <div className="bg-gray-200 p-4 rounded-lg font-medium text-lg mb-4">
                    Farmer Detail
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      {
                        label: "Full Name",
                        value: data.data.data?.customerDetail.customer.fullName,
                      },
                      {
                        label: "Gender",
                        value: data.data.data?.customerDetail.customer.gender,
                      },
                      { label: "Date of Birth", value: formattedDate },
                      {
                        label: "Phone No.",
                        value:
                          data.data.data?.customerDetail.customer.phoneNumber,
                      },
                      {
                        label: "Account No.",
                        value: farmerBankAccount?.accountNumber,
                      },
                      {
                        label: "Financing Bank",
                        value: financingBank?.bankName,
                      },
                      {
                        label: "TIN",
                        value:
                          data.data.data?.customerDetail.customer.tinNumber,
                      },
                      {
                        label: "ID",
                        value:
                          data.data.data?.customerDetail.customer
                            .identificationNumber,
                      },
                      {
                        label: "Primary Cooperative Name",
                        value:
                          data?.data?.data?.customerDetail?.customer
                            ?.primaryCooperativeName || "N/A",
                      },
                      {
                        label: "Union Name",
                        value:
                          data?.data?.data?.customerDetail?.customer
                            ?.unionName || "N/A",
                      },
                    ].map((item, index) => (
                      <div key={index}>
                        <div style={label}>{item.label}</div>
                        <div style={value}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      key: "3",
      label: "FARM DOCUMENTS",
      children: (
        <div>
          {data &&
            data.data &&
            data.data.data &&
            data.data.data?.customerDetail && (
              <div
                style={{
                  fontFamily: "Poppins",
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  borderRadius: 8.5,
                  backgroundColor: "white",
                }}
              >
                <div className="flex w-full mb-[25px]">
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 700,
                        borderRadius: "8.5px",
                        backgroundColor: "white",
                        paddingTop: "33px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#E9EDF4",
                          padding: "15px",
                          borderRadius: "8.5px",
                          fontWeight: 500,
                          fontSize: "24px",
                          marginBottom: "30px",
                        }}
                      >
                        Customer documents
                      </div>

                      <Table
                        columns={columns}
                        dataSource={documents || []}
                        rowKey="docId"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      key: "4",
      label: "FARM LAND",
      children: (
        <div className="rounded-xl overflow-hidden mt-4 pt-2">
          <div className="flex gap-4">
            <span className="text-gray-600 text-xs font-semibold ml-4 ">
              Toggle to view map{" "}
            </span>
            <Switch
              checked={mapView}
              checkedChildren="Text View"
              unCheckedChildren="Map View"
              defaultChecked
              onChange={(val: any) => setMapView(val)}
            />
          </div>

          {isLoaded && mapView && (
            <GoogleMap
              onLoad={handleOnLoad}
              mapContainerClassName={`w-full min-h-[500px] rounded-xl ${
                mapView ? "block" : "hidden"
              }`}
              center={{
                lat: farmLands[0]?.latitude,
                lng: farmLands[0]?.longitude,
              }}
              zoom={15}
            >
              {farmLands &&
                farmLands.map((_data: IAddress) => {
                  if (_data.landCode) {
                    return (
                      <MarkerF
                        key={`${_data.landCode}-${_data.latitude}-${_data.longitude}`}
                        position={{
                          lat: _data.latitude,
                          lng: _data.longitude,
                        }}
                        onClick={() => handleActiveMarker(`${_data.landCode}`)}
                      >
                        {activeMarker === _data.landCode ? (
                          <InfoWindow
                            options={{
                              zIndex: 9999,
                              maxWidth: 400,
                            }}
                            position={{
                              lat: _data.latitude,
                              lng: _data.longitude,
                            }}
                            onCloseClick={() => setActiveMarker("")}
                          >
                            <FarmLandCard
                              key={_data.landCode}
                              region={_data.region}
                              zone={_data.zone}
                              woreda={_data.woreda}
                              kebele={_data.kebele}
                              landSize={_data.landSize}
                            />
                          </InfoWindow>
                        ) : null}
                      </MarkerF>
                    );
                  } else {
                    return <div></div>;
                  }
                })}
            </GoogleMap>
          )}
          {!mapView && (
            <div
              className={`flex flex-col justify-start min-h-[500px] bg-white p-[15px] rounded-xl mt-7.5`}
            >
              {farmLands.map((item: IAddress, index: number) => (
                <>
                  <div
                    key={index}
                    className={`bg-[#E9EDF4] p-[15px] rounded-xl ${
                      index === 0 ? "mb-[30px]" : "mt-4 mb-[30px]"
                    }`}
                    style={{
                      fontWeight: 500,
                      fontSize: "24px",
                    }}
                  >
                    Address
                  </div>
                  <div className="flex flex-col md:flex-row flex-wrap md:gap-6 pl-5">
                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Region</div>
                      <div style={value}>{item.region || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Zone</div>
                      <div style={value}>{item.zone || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Woreda</div>
                      <div style={value}>{item.woreda || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Kebele</div>
                      <div style={value}>{item.kebele || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Land size</div>
                      <div style={value}>{`${item.landSize} ha` || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>PC</div>
                      <div style={value}>
                        {data?.data?.data?.customerDetail?.customer
                          ?.primaryCooperativeName || "N/A"}
                      </div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Union</div>
                      <div style={value}>
                        {data?.data?.data?.customerDetail?.customer
                          ?.unionName || "N/A"}
                      </div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Latitude</div>
                      <div style={value}>{item.latitude || "N/A"}</div>
                    </div>

                    <div
                      className="flex flex-row justify-between md:gap-[10px] border-b-2 md:border-b-0 md:flex-col w-auto"
                      style={{ fontSize: 16, marginBottom: 20 }}
                    >
                      <div style={label}>Longitude</div>
                      <div style={value}>{item.longitude || "N/A"}</div>
                    </div>
                  </div>
                </>
              ))}
            </div>
          )}
          <div>
            <div
              style={{
                backgroundColor: "#E9EDF4",
                padding: "15px",
                borderRadius: "8.5px",
                fontWeight: 500,
                fontSize: "24px",
                marginBottom: "30px",
                marginTop: "30px",
              }}
            >
              Farming Status
            </div>
            <span className="text-gray-600 text-xs font-semibold ml-8 ">
              Toggle to view aggregate{" "}
            </span>
            <OrderTrackingGraph data={graphData} />
          </div>
        </div>
      ),
    },
    {
      key: "5",
      label: "ENTITLEMENT & INSURANCE",
      children: (
        <div className="hidden md:block">
          <div
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              borderRadius: "8.5px",
              backgroundColor: "white",
              paddingTop: "33px",
              paddingBottom: "33px",
            }}
          >
            <div className="flex flex-col w-full mb-[25px] gap-6">
              <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-center">
                <div className="flex flex-col gap-4 w-full">
                  <div
                    style={{
                      backgroundColor: "#E9EDF4",
                      padding: "15px",
                      borderRadius: "8.5px",
                      fontWeight: 500,
                      fontSize: "24px",
                      marginBottom: "30px",
                    }}
                  >
                    Entitlement
                  </div>
                  <Table
                    columns={getEntitlementColumns("summary")}
                    dataSource={tableData}
                  />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex flex-col gap-4 w-full">
                  <div
                    style={{
                      backgroundColor: "#E9EDF4",
                      padding: "15px",
                      borderRadius: "8.5px",
                      fontWeight: 500,
                      fontSize: "24px",
                      marginBottom: "30px",
                    }}
                  >
                    Insurance
                  </div>
                  <Table
                    columns={insuranceColumns}
                    dataSource={insuranceTableData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "6",
      label: "KYF VERIFICATION",
      children: (
        <>
          {data && data.data && data.data.data && data.data.data.contract && (
            <>
              {/* Verification Summary */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-6 bg-blue-600 rounded-full"></div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Summary
                  </h3>
                </div>

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {/* Farmer Information Summary */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Farmer Details
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Full Name</span>
                        <span className="text-sm font-medium text-gray-900">
                          {data.data.data?.customerDetail.customer.fullName ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Phone</span>
                        <span className="text-sm font-medium text-gray-900">
                          {data.data.data?.customerDetail.customer
                            .phoneNumber || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Account No.
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {farmerBankAccount?.accountNumber || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Financing Bank
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {financingBank?.bankName || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">ID Number</span>
                        <span className="text-sm font-medium text-gray-900">
                          {data.data.data?.customerDetail.customer
                            .identificationNumber || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Farm Land Summary */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Farm Land
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {farmLands
                        .slice(0, 1)
                        .map((item: IAddress, index: number) => (
                          <div key={index} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                Region
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {item.region || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                Zone
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {item.zone || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                Woreda
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {item.woreda || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                Land Size
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {item.landSize ? `${item.landSize} ha` : "N/A"}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Documents Summary */}
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Documents
                      </h4>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Total Documents
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {documents?.length || 0} files
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Verified</span>
                        <span className="text-sm font-medium text-gray-900">
                          {documents?.filter((doc) => doc.status === "VERIFIED")
                            .length || 0}{" "}
                          files
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Pending</span>
                        <span className="text-sm font-medium text-gray-900">
                          {documents?.filter((doc) => doc.status === "PENDING")
                            .length || 0}{" "}
                          files
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Primary Cooperative
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {data?.data?.data?.customerDetail?.customer
                            ?.primaryCooperativeName || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* KYF Verification Section */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex w-full">
                  <div className="w-full">
                    {/* KYF - Know Your Farmer verification (per-bank, per-account) */}
                    {user?.roleName !== "ROLE_KFT_SP_ADMIN" &&
                      user?.roleName !== "ROLE_KFT_SUPER_ADMIN" && (
                        <Card
                          className="border-0 shadow-none bg-transparent"
                          title={
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-lg font-semibold text-gray-900">
                                KYF Verification
                              </span>
                              {financingBank && (
                                <Tag
                                  color="blue"
                                  className="border-0 bg-blue-100 text-blue-700 font-medium"
                                >
                                  {financingBank.bankName}
                                </Tag>
                              )}
                              {farmerBankAccount?.accountNumber && (
                                <Tag
                                  color="default"
                                  className="border-0 bg-gray-100 text-gray-700 font-medium"
                                >
                                  {farmerBankAccount.accountNumber}
                                </Tag>
                              )}
                              {farmerBankAccount?.kycStatus && (
                                <Tag
                                  color={
                                    kycApproved
                                      ? "green"
                                      : farmerBankAccount.kycStatus ===
                                          "DECLINED" ||
                                        farmerBankAccount.kycStatus === "FAILED"
                                      ? "red"
                                      : "orange"
                                  }
                                  className="border-0 font-medium"
                                >
                                  {farmerBankAccount.kycStatus}
                                </Tag>
                              )}
                            </div>
                          }
                        >
                          {!isContractBoundToBank ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                              This contract is not yet bound to a financing
                              bank or farmer bank account. Bind a bank/account
                              to the contract before initiating KYF
                              verification.
                            </div>
                          ) : (
                            <VerifyCustomer
                              id={data?.data?.data?.contract?.id}
                              isVerified={kycApproved}
                            />
                          )}
                        </Card>
                      )}
                  </div>
                </div>
              </div>

              {/* Rejection History */}
              {loanStatus === "rejected" &&
                rejectionHistory &&
                rejectionHistory.length > 0 && (
                  <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex flex-col">
                      <div className="w-full flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-6 bg-red-600 rounded-full"></div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Rejection Histories
                          </h3>
                        </div>
                        <Button
                          type="primary"
                          className="bg-blue-600 hover:bg-blue-700 border-0 shadow-sm font-medium"
                          onClick={() => setOpenRejectionModal(true)}
                          icon={<PlusOutlined />}
                        >
                          Add New
                        </Button>
                      </div>

                      <div className="w-full flex flex-col gap-3">
                        {rejectionHistory.map(
                          (item: {
                            id: number;
                            reason: string;
                            status: "ACTIVE" | "RESOLVED";
                          }) => {
                            const isExpanded = expandedList.find(
                              (e) => e.id === item.id
                            )?.isExpanded;

                            return (
                              <div
                                key={item.id}
                                className="w-full flex justify-between items-start p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200 bg-gray-50 hover:bg-white"
                              >
                                {/* Reason Text */}
                                <div className="flex-1 pr-4">
                                  <div className="text-sm text-gray-700 leading-relaxed">
                                    {!isExpanded ? (
                                      <>
                                        {item.reason.substring(0, 120)}
                                        {item.reason.length > 120 && (
                                          <Button
                                            onClick={() => {
                                              const updatedList =
                                                expandedList.filter(
                                                  (e) => e.id !== item.id
                                                );
                                              setExpandedList([
                                                ...updatedList,
                                                {
                                                  id: item.id,
                                                  isExpanded: true,
                                                },
                                              ]);
                                            }}
                                            type="text"
                                            className="text-blue-600 hover:text-blue-800 font-medium text-xs ml-1"
                                          >
                                            Read more
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {item.reason}
                                        <Button
                                          type="text"
                                          className="text-blue-600 hover:text-blue-800 font-medium text-xs ml-2"
                                          onClick={() => {
                                            const updatedList =
                                              expandedList.filter(
                                                (e) => e.id !== item.id
                                              );
                                            setExpandedList([
                                              ...updatedList,
                                              {
                                                id: item.id,
                                                isExpanded: false,
                                              },
                                            ]);
                                          }}
                                        >
                                          Show less
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Status Switch */}
                                <div className="flex-shrink-0">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        item.status === "ACTIVE"
                                          ? "bg-red-100 text-red-700"
                                          : "bg-green-100 text-green-700"
                                      }`}
                                    >
                                      {item.status}
                                    </span>
                                    <Switch
                                      checkedChildren=""
                                      unCheckedChildren=""
                                      checked={item.status === "ACTIVE"}
                                      onChange={(checked: boolean) => {
                                        setSelectedRejectionStatusId(item.id);
                                        setIsRejectionStatusUpdatePending(true);
                                        handleUpdateRejectionStatus(
                                          item.id,
                                          checked
                                        );
                                        setTimeout(
                                          () =>
                                            setIsRejectionStatusUpdatePending(
                                              false
                                            ),
                                          1000
                                        );
                                      }}
                                      loading={
                                        selectedRejectionStatusId === item.id &&
                                        isRejectionStatusUpdatePending
                                      }
                                      className={
                                        item.status === "ACTIVE"
                                          ? "bg-red-600"
                                          : "bg-gray-300"
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </>
          )}
        </>
      ),
    },
    {
      key: "7",
      label: "LOAN STATUS",
      children: (
        <>
          {data && data.data && data.data.data && data.data.data.contract && (
            <>
              {/* Bank Registration Alert */}
              {!kycApproved && (
                <div className="mb-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg mr-6 p-4">
                    <div className="flex items-center gap-3">
                      <CircleAlert className="text-orange-600 text-lg" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-orange-800 font-semibold">
                            Action Required
                          </span>
                          <Tag
                            color="orange"
                            className="border-0 bg-orange-100 text-orange-700 font-medium"
                          >
                            KYF Verification
                          </Tag>
                          {financingBank && (
                            <Tag
                              color="blue"
                              className="border-0 bg-blue-100 text-blue-700 font-medium"
                            >
                              {financingBank.bankName}
                            </Tag>
                          )}
                        </div>
                        <p className="text-orange-600 text-sm">
                          {!isContractBoundToBank
                            ? "This contract is not yet bound to a financing bank or farmer bank account. Bind one before requesting KYF verification."
                            : "Please complete the KYF verification process for the bound bank account to enable loan status updates."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Alert when bank-account KYC is approved */}
              {kycApproved && (
                <div className="mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="text-green-600 text-lg" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-green-800 font-semibold">
                            Bank Registered
                          </span>
                          <Tag
                            color="green"
                            className="border-0 bg-green-100 text-green-700 font-medium"
                          >
                            Ready for Verification
                          </Tag>
                          {financingBank && (
                            <Tag
                              color="blue"
                              className="border-0 bg-blue-100 text-blue-700 font-medium"
                            >
                              {financingBank.bankName}
                            </Tag>
                          )}
                          {farmerBankAccount?.accountNumber && (
                            <Tag
                              color="default"
                              className="border-0 bg-gray-100 text-gray-700 font-medium"
                            >
                              {farmerBankAccount.accountNumber}
                            </Tag>
                          )}
                        </div>
                        <p className="text-green-600 text-sm">
                          Bank registration completed successfully for this
                          account. You can now proceed with loan status updates.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex w-full">
                  <div className="w-full">
                    {/* Loan Approval Table */}
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Loan Entitlements
                        </h3>
                      </div>
                      <Table
                        columns={getEntitlementColumns("details")}
                        dataSource={tableData}
                        className="rounded-lg border border-gray-200 overflow-hidden"
                      />
                    </div>

                    {/* Summary Section */}
                    <div className="mb-8">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              />
                            </svg>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Loan Summary
                          </h3>
                        </div>
                      </div>

                      {/* Loan Details Grid */}
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {loanDetails.map((item, index) => {
                          const isTotal = item.label.includes("Credit Amount");
                          const isInsurance = item.label.includes("Insurance");

                          return (
                            <div
                              key={index}
                              className={`
                            bg-white rounded-xl border p-5 shadow-sm transition-all duration-200 hover:shadow-md
                            ${
                              isTotal
                                ? "border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 md:col-span-2 lg:col-span-3"
                                : "border-gray-200"
                            }
                          `}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`
                                  w-3 h-3 rounded-full
                                  ${
                                    isTotal
                                      ? "bg-green-500 animate-pulse"
                                      : isInsurance
                                      ? "bg-purple-500"
                                      : "bg-blue-500"
                                  }
                                `}
                                  ></div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-600 mb-1">
                                      {item.label}
                                    </p>
                                    <p
                                      className={`
                                    text-2xl font-bold
                                    ${
                                      isTotal
                                        ? "text-green-900"
                                        : "text-gray-900"
                                    }
                                  `}
                                    >
                                      {item.value}
                                    </p>
                                  </div>
                                </div>
                                {item.label.includes("ETB") && (
                                  <span
                                    className={`
                                  text-xs font-medium px-2 py-1 rounded-full
                                  ${
                                    isTotal
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-600"
                                  }
                                `}
                                  >
                                    ETB
                                  </span>
                                )}
                              </div>

                              {isTotal && (
                                <div className="mt-3 pt-3 border-t border-green-200">
                                  <p className="text-xs text-green-600">
                                    Total credit amount allocated for this loan
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rejection History */}
              {loanStatus === "rejected" &&
                rejectionHistory &&
                rejectionHistory.length > 0 && (
                  <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex flex-col">
                      <div className="w-full flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-6 bg-red-600 rounded-full"></div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Rejection History
                          </h3>
                        </div>
                        <Button
                          type="primary"
                          className="bg-blue-600 hover:bg-blue-700 border-0 shadow-sm font-medium"
                          onClick={() => setOpenRejectionModal(true)}
                          icon={<PlusOutlined />}
                          disabled={!kycApproved}
                        >
                          Add New
                        </Button>
                      </div>

                      <div className="w-full flex flex-col gap-3">
                        {rejectionHistory.map(
                          (item: {
                            id: number;
                            reason: string;
                            status: "ACTIVE" | "RESOLVED";
                          }) => {
                            const isExpanded = expandedList.find(
                              (e) => e.id === item.id
                            )?.isExpanded;

                            return (
                              <div
                                key={item.id}
                                className="w-full flex justify-between items-start p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200 bg-gray-50 hover:bg-white"
                              >
                                {/* Reason Text */}
                                <div className="flex-1 pr-4">
                                  <div className="text-sm text-gray-700 leading-relaxed">
                                    {!isExpanded ? (
                                      <>
                                        {item.reason.substring(0, 120)}
                                        {item.reason.length > 120 && (
                                          <Button
                                            onClick={() => {
                                              const updatedList =
                                                expandedList.filter(
                                                  (e) => e.id !== item.id
                                                );
                                              setExpandedList([
                                                ...updatedList,
                                                {
                                                  id: item.id,
                                                  isExpanded: true,
                                                },
                                              ]);
                                            }}
                                            type="text"
                                            className="text-blue-600 hover:text-blue-800 font-medium text-xs ml-1"
                                          >
                                            Read more
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {item.reason}
                                        <Button
                                          type="text"
                                          className="text-blue-600 hover:text-blue-800 font-medium text-xs ml-2"
                                          onClick={() => {
                                            const updatedList =
                                              expandedList.filter(
                                                (e) => e.id !== item.id
                                              );
                                            setExpandedList([
                                              ...updatedList,
                                              {
                                                id: item.id,
                                                isExpanded: false,
                                              },
                                            ]);
                                          }}
                                        >
                                          Show less
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Status Switch */}
                                <div className="flex-shrink-0">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        item.status === "ACTIVE"
                                          ? "bg-red-100 text-red-700"
                                          : "bg-green-100 text-green-700"
                                      }`}
                                    >
                                      {item.status}
                                    </span>
                                    <Switch
                                      checkedChildren=""
                                      unCheckedChildren=""
                                      checked={item.status === "ACTIVE"}
                                      onChange={(checked: boolean) => {
                                        setSelectedRejectionStatusId(item.id);
                                        setIsRejectionStatusUpdatePending(true);
                                        handleUpdateRejectionStatus(
                                          item.id,
                                          checked
                                        );
                                        setTimeout(
                                          () =>
                                            setIsRejectionStatusUpdatePending(
                                              false
                                            ),
                                          1000
                                        );
                                      }}
                                      loading={
                                        selectedRejectionStatusId === item.id &&
                                        isRejectionStatusUpdatePending
                                      }
                                      className={
                                        item.status === "ACTIVE"
                                          ? "bg-red-600"
                                          : "bg-gray-300"
                                      }
                                      disabled={!kycApproved}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </>
          )}
        </>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-8 pt-6 min-h-[600px]">
      <Modal
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 27.43,
              color: "black",
            }}
          >
            Rejection Reason
          </div>
        }
        open={openRejectionModal}
        centered
        onOk={handleAddRejectionReason}
        okButtonProps={{
          disabled: rejectionReason.length === 0,
        }}
        onCancel={() => setOpenRejectionModal(false)}
        closable
        destroyOnClose={true}
      >
        <TextArea
          placeholder="Insert the rejection history"
          onChange={(e) => setRejectionReason(e.target.value)}
        />
      </Modal>

      <Tabs
        style={{
          background: "white",
          fontWeight: 700,
          minHeight: contractLoading ? "70vh" : "auto",
          paddingLeft: 20,
        }}
        className="text-black"
        defaultActiveKey="1"
        items={tabItems}
        onChange={(key) => setActiveTab(key)}
      />
    </div>
  );
};

export default ContractDetailPage;
